import {
  Component, OnInit, HostBinding, ChangeDetectorRef, ViewChild
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, ValidatorFn, AbstractControl, UntypedFormArray, Validators } from '@angular/forms';
import { Observable } from 'rxjs';

import * as _ from 'lodash';

// data, models, helpers
import { segmentAnimation } from '../base/animations';
import { hasServerError, getFieldByName } from 'utils/utils';
import { createOption } from 'shared/form/select/option-helpers';
import { UserAttribute } from '../../models/user-attribute.model';
import { attributesSegmentData } from '../../segment-data/attributes-segment-data';
import { Option } from 'shared/form/select/option.interface';
import { getValidationByName } from 'shared/validation/validation-helpers';

// components
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { SuggestedAttributesComponent } from './suggested-attributes/suggested-attributes.component';
import { SuggestedAttributesModalComponent } from './suggested-attributes-modal/suggested-attributes-modal.component';
import { DetailsControlComponent } from 'shared/details-page/details-control/details-control.component';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';

// services
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { BrandService } from 'shared/brand/brand.service';
import { AttributesService } from './attributes.service';
import { forkJoin } from 'rxjs';
import { ProductPanelComponent } from '../../../product-panel/product-panel.component';


@Component({
  moduleId: module.id,
  selector: 'attributes-segment',
  templateUrl: 'attributes.component.html',
  styleUrls: [
    '../base/_base-segment.component.scss',
    '../base/base-attributes-table.scss',
    'attributes.component.scss'
  ],
  animations: [segmentAnimation],
})
export class AttributesComponent extends BaseSegmentComponent implements OnInit {

  @ViewChild(SuggestedAttributesModalComponent, {static: false})
  suggestedAttributesModal: SuggestedAttributesModalComponent | void;

  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'attributes';
  pathToScores: string = 'segments.attributes.tallies';

  userUnitsData: Array<Option> = [];
  userHeaders: Array<any> = [];
  userFields: Array<any> = [];
  hasPartType: boolean;
  choosingNewAttributeType: boolean = false;
  controlClassPrefix: string = 'control-row-';

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    private attributesService: AttributesService,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get itemDetailAttributes(): Array<any> {
    return _.get(this.itemDetail, 'attributes');
  }

  // needs for drag'n'drop
  // TODO: replace itemDetailAttributes on itemDetailData in future
  get itemDetailData(): Array<any> {
    return this.itemDetailAttributes;
  }

  get formArray(): UntypedFormArray {
    return this.attributes;
  }

  get attributes(): UntypedFormArray {
    return this.segmentForm.get('attributes') as UntypedFormArray;
  }

  get isEmpty(): boolean {
    return !this.attributesSize;
  }

  get attributesSize(): number {
    return _.size(this.itemDetailAttributes);
  }

  get itemId(): any {
    return _.get(this.itemDetail, 'itemId');
  }

  get partTerminologyId(): any {
    return _.get(this.itemDetail, 'partTerminology.id');
  }

  get partCategory(): string|number {
    return _.get(this.itemDetail, 'partTerminology.category.name');
  }

  get partSubcategory(): string|number {
    return _.get(this.itemDetail, 'partTerminology.subcategory.name');
  }

  get isMissingAttribute(): boolean {
    return _.get(this.scoreData, 'segments.attributes.non-standard.missing.valid') === 0;
  }

  get defaultOption(): Option {
    return createOption('', '-');
  }

  /*******************************************
  * COMPONENT SETTERS
  *******************************************/

  set itemDetailAttributes(data: Array<any>) {
    _.set(this.itemDetail, 'attributes', data);
  }


  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();

    this.initFields();
    this.updateUom();

    this.segmentForm = this.fb.group({
      attributes: this.fb.array([])
    });

    if (this.itemDetail) {
      this.handleItemDetail(this.itemDetail);
    }
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onAddSuggestedAttributes(attributes: Array<any>) {
    _.forEach(attributes, attribute => {
      attribute.is_padb = 'Y';
    });
    this.addAttributes(attributes);
    this.markAsDirty();
    this.ref.markForCheck();
  }

  onSavedCallback() {
    if (this.suggestedAttributesModal instanceof SuggestedAttributesModalComponent) {
      this.suggestedAttributesModal.clearFilters();
    }
  }

  onAddNewSuggesteAttribute() {
    if (this.suggestedAttributesModal instanceof SuggestedAttributesModalComponent) {
      this.suggestedAttributesModal.show();
    }
  }

  onAddNewTypeAttributeClick(withUnits: boolean) {
    this.addCustomAttribute(withUnits);
  }

  onDeleteValueClick(valueControl: UntypedFormControl, formGroup: UntypedFormGroup, rowIndex: number, valueIndex: number) {
    const formArray: any = formGroup.controls.value;
    const index = formArray.controls.indexOf(valueControl);
    if (index < 0) { return; }
    formArray.removeAt(index);
    _.pullAt(this.itemDetailAttributes[rowIndex].value, [valueIndex]);
    if (formArray.controls.length === 1) {
      delete this.itemDetailAttributes[rowIndex].is_value_list;
    }
    this.scorecardConnectorService.controlRemoved.emit({
      isPadb: true, index: rowIndex, valueIndex
    });
    formArray.markAsDirty();
    const uomArray: any = formGroup.controls.uom_array;
    if (uomArray) {
      uomArray.removeAt(index);
      uomArray.markAsDirty();
    }
  }

  onAddValueClick(formGroup: UntypedFormGroup, rowIndex: number) {
    const formArray: any = formGroup.controls.value;
    const attribute = this.itemDetailAttributes[rowIndex];
    let validators = [];
    if (this.isPadb(attribute)) {
      validators = this.getMetaDataValidators(attribute.meta_data);
    } else {
      validators = this.getFieldValidators(this.getAttributeFieldByName('value'));
    }
    if (typeof attribute.value[0] === 'object') {
      attribute.value.push({value: '', uom: ''});
    } else {
      attribute.value.push('');
    }
    formArray.push(new UntypedFormControl('', validators));
    this.scorecardConnectorService.controlAdded.emit({isPadb: true, index: rowIndex});
    formArray.markAsDirty();
    const uomArray: any = formGroup.controls.uom_array;
    if (uomArray) {
      uomArray.push(new UntypedFormControl(''));
      uomArray.markAsDirty();
    }
    this.itemDetailAttributes[rowIndex].is_value_list = true;
    delete this.itemDetailAttributes[rowIndex].not_list;
  }

  onAddFirstValueClick(formGroup: UntypedFormGroup, index: number) {
    this.onAddValueClick(formGroup, index);
  }

  onSelectChange(selectedItem: any, control: any) {
    control.setValue(selectedItem.option);
    this.attributes.markAsDirty();
  }

  onRowRemove(formGroup: UntypedFormGroup) {
    const index = this.attributes.controls.indexOf(formGroup);
    this.attributes.removeAt(index);
    this.itemDetailAttributes.splice(index, 1);
    if (this.segmentScores) {
      this.segmentScores.splice(index, 1);
      this.scorecardConnectorService.detectChangesInScorecard.emit();
    }
    this.attributes.markAsDirty();
    if (!this.bulkEdit) {
      if (this.suggestedAttributesModal instanceof SuggestedAttributesModalComponent) {
        this.suggestedAttributesModal.removeFilters([formGroup.get('attribute_id').value]);
      }
    }
  }

  onValueStatusChange(status: any, itemName: string, index: number, valueIndex: number) {
    this.scorecardConnectorService.controlStatusChanged.emit({
      status,
      index,
      item: this.getAttributeFieldByName(itemName),
      valueIndex: (this.itemDetailAttributes[index].is_value_list ? valueIndex : null)
    });
  }

  onNameStatusChange(status: any, itemName: string, index: number) {
    this.scorecardConnectorService.controlStatusChanged.emit({
      status, index, item: this.getAttributeFieldByName(itemName),
    });
  }

  onNamePopulatedChange(populated: number, itemName: string, index: number) {
    this.scorecardConnectorService.controlPopulatedChanged.emit({
      populated, index, item: this.getAttributeFieldByName(itemName),
    });
  }

  onValuePopulatedChange(populated: any, itemName: string, index: number, valueIndex: number) {
    this.scorecardConnectorService.controlPopulatedChanged.emit({
      populated,
      index,
      item: this.getAttributeFieldByName(itemName),
      valueIndex: (this.itemDetailAttributes[index].is_value_list ? valueIndex : null)
    });
  }

  onAttrControlFocus(fieldMap: string, index: number, valueIndex: number) {
    this.scorecardConnectorService.controlFocused.emit({
      index,
      valueIndex: (this.itemDetailAttributes[index].is_value_list ? valueIndex : null),
      item: this.getAttributeFieldByName(fieldMap),
    });
  }

  onAttributeNameChange(name: string, index: number) {
    this.itemDetailAttributes[index]['name'] = name;
    this.scorecardConnectorService.detectChangesInScorecard.emit();
  }

  onSelectedChange($event, formGroup, j) {
    if (formGroup.get('uom_array')) {
      const control = formGroup.get('uom_array').controls[j];
      control.setValue($event.option);
      this.attributes.markAsDirty();
    }
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getSegmentFormData(): any {
    return _.map(this.attributes.getRawValue(), (attribute: any, i: number) => {

      // an attribute should have next fields:
      //
      // attribute_id - string
      // is_padb - "Y"/"N"
      // uom - string or ""
      // value - string or array
      // style_id? - number
      // assignment_id? - number

      let value;
      // if different UOMs in one control
      if (attribute.uom_array) {
        value = attribute.value?.map((v, index) => {
          return {
            value: v,
            uom: attribute.uom_array[index] ?? ''
          };
        });
      }
      const attributeToSave: any = {
        attribute_id: this.isPadb(attribute) ? attribute.attribute_id : attribute.name,
        is_padb: attribute.is_padb ? attribute.is_padb : 'N',
        uom: attribute.uom ? attribute.uom : '',
        value: value || (attribute.is_padb === 'N' && !this.itemDetailAttributes[i]?.is_value_list ? attribute.value[0] : attribute.value),
      };

      // handle assignment_id
      if (attribute.assignment_id) {
        attributeToSave.assignment_id = attribute.assignment_id;
      }

      // handle style
      if (attribute.style_id) {
        attributeToSave.style_id = attribute.style_id;
      }
      // save data for the bulk edit mode
      if (this.bulkEdit) {
        attributeToSave.name = attribute.name;
        attributeToSave.uom_description = attribute.uom_description;
      }
      return attributeToSave;
    });
  }

  public updateScoreData(data: any): void {
    // before scores update check whether previous scorecard had one missing attribute and remove it
    this.removeMissingAttribute();
    super.updateScoreData(data);
    // if new scorecard has missing attribute error then add at least one empty attribute
    if (this.isMissingAttribute && _.size(this.itemDetail) && !_.size(this.itemDetailAttributes)) {
      this.addCustomAttribute(true, true);
      this.showSegment();
    }
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  // replace parent method:
  protected setBulkData() {
    this.hasPartType = true;
    this.setData(this.getBulkSegmentData(this.segmentName));
  }

  // replace parent method:
  protected setData(itemDetail: any) {
    this.hasPartType = !!(itemDetail.partTerminology && itemDetail.partTerminology.id);
    if (_.size(itemDetail.attributes)) {
      // find PAdb attributes and create requests for them
      const requestArray: any[] = [];
      const padbFilters: any[] = [];
      _.forEach(itemDetail.attributes, (item: any) => {
        if (this.isPadb(item)) {
          requestArray.push(this.attributesService.getPadbAttributeDetails(item.assignment_id));
        }
      });
      // if we need to request additional data for PAdb attributes -
      // request and then set data
      if (requestArray.length) {
        forkJoin(requestArray)
        .subscribe((results: any) => {
          _.forEach(results, (result: any) => {
            let item = _.find(itemDetail.attributes, {assignment_id: result.assignment_id});
            if (item) {
              item = Object.assign(item, result);
            }
          });
          this.updateAttributesForm();
          this.showSegment();
        });
      } else {
        this.updateAttributesForm();
        this.showSegment();
      }
    } else {
      if (this.isMissingAttribute) {
        this.addCustomAttribute(true, true);
      }
      this.showSegment();
    }
  }

  protected maxLength(name: string, index: number, dataItem?: any): number {
    if (name === 'value' && _.get(dataItem, 'is_padb') === 'Y') {
      return _.toNumber(_.get(dataItem, 'meta_data.rules.max_length', 2000));
    } else {
      let ruleName = _.get(this.segmentScores, `[${index}].${name}.rule.validation_rule`);
      if (!ruleName) {
        ruleName = this.getScorecardFieldValidationRule(name);
      }
      return _.get(this.getAttributeFieldByName(name), `validation.${ruleName}.maxLength`);
    }
  }

  protected updateControlsValidation(): void {
    _.each(this.itemDetailAttributes, (attribute: any, index: number) => {
      _.each(this.userFields, (item: SegmentFieldItem) => {
        const formControl: any = this.attributes.at(index).get(item.field_map);
        if (!formControl) { return; }
        const validators: any = this.getFieldValidators(item);
        if (formControl.controls) {
          // FormArray
          _.each(formControl.controls, (control: AbstractControl) => {
            this.updateControlValidators(control, validators);
          });
        } else {
          // FormControl
          this.updateControlValidators(formControl, validators);
        }
      });
    });
  }

  protected getItemFieldScoreFromEvent($event: any) {
    if (_.isNil($event.valueIndex)) {
      return this.getScoreItem($event.item, $event.index);
    } else {
      return this.getScoreIndexItem($event.item, $event.index, $event.valueIndex);
    }
  }

  /*
    * $event: {item: SegmentFieldItem, index: number}
   */
  protected handleScoreDetailsExpand($event: any): void {
    if (!this.editMode) { return; }
    const fieldMap = $event.item.field_map;
    const controls = this.getRowControls($event.index, fieldMap);
    _.defer(() => {
      if (_.size(controls)) { controls[0].focusControl(fieldMap); }
    });
  }

  /*
    * data: {item: SegmentFieldItem, index: number}
   */
  protected findControlViaData(data: any): DetailsControlComponent {
    return _.nth(this.getRowControls(data.index, data.item.field_map), 0);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private initFields(): void {
    this.userHeaders = _.filter(attributesSegmentData, (item: any) => {
      switch (item.item_name) {
        case 'name':
        case 'value':
        case 'uom_description':
        case 'style_id':
          return true;
        default:
          return false;
      }
    });

    this.userFields = _.filter(attributesSegmentData, (item: any) => {
      switch (item.item_name) {
        case 'name':
        case 'value':
        case 'uom_description':
        case 'uom':
        // only for PAdb
        // falls through
        case 'attribute_id':
        case 'style_id':
        case 'is_padb':
        case 'assignment_id':
          return true;
        default:
          return false;
      }
    });
  }

  private updateUom(): void {
    this.attributesService.getUom()
    .subscribe((data: any) => {
      // this.unitsData = data;
      this.userUnitsData = data;
      this.userUnitsData.splice(0, 0, this.defaultOption);
      this.ref.markForCheck();
      // if (this.bulkEdit) {
      //   this.addLookupPlaceholder(this.unitsData);
      // }
    });
  }

  private updateAttributesForm(): void {
    // create formArray
    this.segmentForm.setControl('attributes', this.fb.array([]));

    // handle each attribute
    this.itemDetailAttributes = this.itemDetailAttributes.map((item: any) => {
      return this.handleAttribute(item);
    });

    // add attribute to formArray
    _.forEach(this.itemDetailAttributes, (attribute: any) => {
      // add attribute padb error if it exists
      this.checkInvalidPadb(attribute);
      this.attributes.push(this.createAttributeFG(attribute));
    });
    this.markAsPristine();
    this.ref.markForCheck();
  }

  private createAttributeFG(attribute: any): UntypedFormGroup {
    const attributeModel = this.createAttributeModel(attribute);
    return this.fb.group(attributeModel);
  }

  private handleAttribute(attribute: any) {
    const handledAttribute = {...attribute};

    // set assignment_id for non-PAdb attributes
    if (!handledAttribute.assignment_id) {
      handledAttribute.assignment_id = null;
    }

    // convert attribute value to array
    if (!Array.isArray(handledAttribute.value)) {
      handledAttribute.not_list = true;
      handledAttribute.value = [handledAttribute.value];
    } else {
      // used in scorecard to identify list elements
      if (!handledAttribute.not_list) {
        handledAttribute.is_value_list = true;
      }
    }

    // normalize mixed value arrays (e.g. ["10", {value:"15",uom:""}] from pre-SPD2-5406 saves)
    if (handledAttribute.value.some((v: any) => v !== null && typeof v === 'object')) {
      handledAttribute.value = handledAttribute.value.map((v: any) =>
        (v !== null && typeof v === 'object') ? v : { value: String(v ?? ''), uom: handledAttribute.uom ?? '' }
      );
    }

    // if it's no valid_attribute_values field - add it
    if (handledAttribute.valid_attribute_values && handledAttribute.valid_attribute_values.length) {
      handledAttribute.valid_attribute_values = handledAttribute.valid_attribute_values.map((value: any) => {
        return createOption(value);
      });
      // add default option
      handledAttribute.valid_attribute_values.splice(0, 0, this.defaultOption);
    } else {
      handledAttribute.valid_attribute_values = null;
    }

    // if uom is select
    if (handledAttribute.meta_uom_codes) {
      // add default option
      handledAttribute.meta_uom_codes.splice(0, 0, createOption('', '-'));
    }

    // if it's no style_id field - add it
    if (!handledAttribute.style_id) {
      handledAttribute.style_id = null;
    }
    // if style is select
    if (handledAttribute.styles && handledAttribute.styles.length) {
      // add default option
      handledAttribute.styles.splice(0, 0, this.defaultOption);
    }

    // is PAdb
    if (!handledAttribute.is_padb) {
      handledAttribute.is_padb = 'N';
    }

    return handledAttribute;
  }

  private createAttributeModel(attribute: any): UserAttribute {
    const model = new UserAttribute();

    _.each(this.userFields, (field: SegmentFieldItem) => {
      if (!field.field_map) { return; }
      const index = _.indexOf(this.itemDetailAttributes, attribute);
      let validators: any = this.getFieldValidators(field);
      if (field.field_map === 'value') {
        // value is Array
        // if attrubute is padb & value has meta_data,
        // then bind min/max length from meta_data
        if (!attribute.valid_attribute_values && attribute.meta_data) {
          validators = this.getMetaDataValidators(attribute.meta_data);
        }
        // if different UOMs in one control
        if (attribute.value[0] && typeof attribute.value[0] === 'object') {
          const valueFormGroups = attribute.value.map((val: any, valueIndex: number) => {
            return new UntypedFormControl(val.value, validators);
          });
          const valueFormArray = new UntypedFormArray(valueFormGroups);
          _.set(model, field.field_map, valueFormArray);

          const uomFormGroups = attribute.value.map((val: any, valueIndex: number) => {
            return new UntypedFormControl(val.uom);
          });
          const uomFormArray = new UntypedFormArray(uomFormGroups);
          _.set(model, 'uom_array', uomFormArray);
        } else if (this.isPadb(attribute)
          ? attribute.uom || (attribute.meta_uom_codes && attribute.meta_uom_codes.length)
          : !_.isNil(attribute.uom)) {
          // if different UOMs is possible
          const valueFormGroups = attribute.value.map((val: any, valueIndex: number) => {
            return new UntypedFormControl(val, validators);
          });
          const valueFormArray = new UntypedFormArray(valueFormGroups);
          _.set(model, field.field_map, valueFormArray);

          const uomFormGroups = attribute.value.map(() => {
            return new UntypedFormControl(attribute.uom);
          });
          const uomFormArray = new UntypedFormArray(uomFormGroups);
          _.set(model, 'uom_array', uomFormArray);
        } else {
          const valueFormGroups = attribute.value.map((val: any, valueIndex: number) => {
            return new UntypedFormControl(val, validators);
          });
          const valueFormArray = new UntypedFormArray(valueFormGroups);
          _.set(model, field.field_map, valueFormArray);
        }
      } else {
        const value = _.get(attribute, field.item_name, '');
        _.set(model, field.field_map, new UntypedFormControl(value, validators));
      }
    });
    return model;
  }

  private addAttributes(attributes?: any) {
    if (!attributes.length) {
      attributes = [];
    }
    // if we have missing attribute item
    // we have to remove it after adding new attribute
    this.removeMissingAttribute();

    _.forEach(attributes, (attribute: any) => {
      if (!attribute.attribute_id) {
        attribute.attribute_id = attribute.id !== undefined ? attribute.id : attribute.name;
      }
      delete attribute.id;
      if (this.isPadb(attribute)) {
        attribute.value = [''];
      } else {
        attribute.value = '';
      }
      const newAttr = this.handleAttribute(attribute);
      const group = this.createAttributeFG(newAttr);
      if (!this.itemDetailAttributes) {
        this.itemDetail.attributes = [];
      }
      // add score to the scorecard before adding control
      this.scorecardConnectorService.controlAdded.emit({isPadb: this.isPadb(attribute), attribute: newAttr});
      this.itemDetailAttributes.push(newAttr);
      this.attributes.push(group);
    });
  }

  private getValue(formGroup: UntypedFormGroup) {
    return formGroup.controls.value.value.join('<br>');
  }

  private getUnits(item: any) {
    return item.meta_uom_codes ? item.meta_uom_codes : this.userUnitsData;
  }

  private getStyleTitle(styleId: string, index: number) {
    const item: any = _.find(this.itemDetailAttributes[index].styles, {option: styleId});
    return item ? item.title : '';
  }

  private getMetaDataValidators(metaData: any): Array<any> {
    const regex = _.get(metaData, 'rules.regex', null);
    const decimalPoints = _.toNumber(_.get(metaData, 'rules.decimal_points', null));
    const dataType = _.get(metaData, 'data_type');
    let decimalValidator: any;
    if (decimalPoints) {
      decimalValidator = Validators.pattern(new RegExp(`^\\d+\\.\\d{${decimalPoints}}$`));
    }
    return this.initValidators({
      'required': true,
      'minLength': _.toNumber(_.get(metaData, 'rules.min_length', 1)),
      'maxLength': _.toNumber(_.get(metaData, 'rules.max_length', 2000)),
      'pattern': regex ? new RegExp(regex, 'g') : null,
      'validator': decimalValidator,
      'number': (dataType === 'number' || dataType === 'decimal'),
    });
  }


  private isInputNumber(item: any): boolean {
    if (this.isPadb(item)) {
      return _.includes(['number', 'decimal'], _.get(item, 'meta_data.data_type'));
    }
    return false;
  }

  private isInputDecimal(item: any): boolean {
    if (this.isPadb(item)) {
      return _.get(item, 'meta_data.data_type') === 'decimal';
    } else {
      return false;
    }
  }

  private hasUomControl(i: number): boolean {
    return !_.isNil(this.itemDetailAttributes[i].uom) ||
    (this.itemDetailAttributes[i].meta_uom_codes && this.itemDetailAttributes[i].meta_uom_codes.length);
  }

  private hasStyleControl(i: number): boolean {
    return this.itemDetailAttributes[i].styles && this.itemDetailAttributes[i].styles.length > 0;
  }

  private hasDeleteBtn(formGroup: any, j: number): boolean {
    return formGroup.controls.value.controls.length > 1 && j !== 0;
  }

  private hasAddBtn(i: number, isLast: boolean): boolean {
    return !this.isItemMissing(this.itemDetailAttributes[i]) && isLast;
  }

  private getErrorPath(attributeIndex: number, name: string, valueIndex?: number): string {
    const controlPath = !_.isNil(valueIndex) ? `.${valueIndex}` : '';
    return `attributes.${attributeIndex}.${name}${controlPath}`;
  }

  private getAttributeFieldByName(name: string): any {
    return this.getFieldByName(name, this.userFields);
  }

  private isPadbInvalid(attribute: any): boolean {
    return attribute.invalidPadb;
  }

  isItemInvalidInScores(item: any): boolean {
    if (!_.has(this.segmentScores, item.item_name)) { return false; }
    const itemSegment = _.get(this.segmentScores, item.item_name);
    return !itemSegment.valid;
  }

  /*
    * check if PAdb ID is not valid (see validation cases SPD2-3305)
   */
  private checkInvalidPadb(attribute: any) {
    const index = _.indexOf(this.itemDetailAttributes, attribute);
    const hasIdError = hasServerError(
      this.itemDetailValidation,
      this.getErrorPath(index, 'attribute_id'),
      undefined,
      undefined,
    );
    if (hasIdError) {
      attribute.invalidPadb = true;
      attribute.padbError = this.getPadbErrorMessage(index);
    }
  }

  /*
    * Returns error message for padb attribute for the next validation cases
    * SPD2-3305
   */
  private getPadbErrorMessage(index: number): string|null {
    const rule = _.get(this.getScorecardField('name'), 'validation_rule');
    const error: any = _.find(_.get(this.itemDetailValidation, rule), (item: any) => {
      return item.path === `${index}.attribute_id`;
    });
    return _.get(error, 'message');
  }

  private getScoreItem(item: SegmentFieldItem, index: number|string): any {
    return _.get(this.segmentScores, `${index}.${item.item_name}`);
  }

  private getScoreIndexItem(item: SegmentFieldItem, index: number|string, propertyIndex: number): any {
    return _.get(this.segmentScores, `${index}.${item.item_name}[${propertyIndex}]`);
  }

  private addCustomAttribute(withUnits: boolean, isMissing?: boolean) {
    const newAttr: any = {
      name: '',
      value: [''],
      is_custom: true,
      not_list: true,
    };
    if (withUnits) {
      newAttr.uom_description = '';
      newAttr.uom = '';
    }
    if (isMissing) { newAttr.isMissing = true; }
    this.addAttributes([newAttr]);
    if (!isMissing) { this.markAsDirty(); }
    this.choosingNewAttributeType = false;
    this.scorecardConnectorService.detectChangesInScorecard.emit();
  }

  private getScorecardFieldValidationRule(itemName: string): string {
    return _.get(this.getScorecardField(itemName), 'validation_rule');
  }

  private isPadb(item: any): boolean {
    return _.get(item, 'is_padb') === 'Y';
  }

  private isItemMissing(item: any): boolean {
    return _.get(item, 'isMissing');
  }

  /*
    * Disable edit if item is missing row
    * Enable edit in edit mode
   */
  private canEditAttribute(item: any): boolean {
    return this.isItemMissing(item) ? false : this.editMode;
  }

  /*
    * We remove missing attribute item after user add new attribute
    * Or after changing scorecard if new scorecard doesn't have missing attribute error
   */
  private removeMissingAttribute(): void {
    if (this.isMissingAttribute && this.attributesSize === 1) {
      const missingItem = _.find(this.itemDetailAttributes, ['isMissing', true]);
      if (missingItem) {
        this.attributes.removeAt(0);
        this.segmentScores = [];
        this.itemDetail.attributes = [];
        this.scorecardConnectorService.detectChangesInScorecard.emit();
      }
    }
  }
  private getUomFormGroup(formGroup) {
    return formGroup.get('value');
  }
  private getUom(formGroup, j) {
    return 'uom';
  }
  private getUomItem(formGroup, j, i) {
    if (formGroup.get('uom_array')) {
      const value = formGroup.get('uom_array')?.value[j];
      if (!value) {
        return null;
      }
      const unit = this.getUnits(this.itemDetailAttributes[i]).find(u => u.option === value);
      return unit;
    } else {
      return null;
    }
  }
}
